import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import client from '@/api/client';
import type { UserRole } from '@/api/types';

interface Props {
  role: UserRole;
  onClose: () => void;
  /** TENANT only — deletion is immediate, not a request, so the caller must log the user out. */
  onDeleted?: () => void;
}

export function AccountDeletionModal({ role, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const isTenant = role === 'TENANT';

  const deletionMutation = useMutation({
    mutationFn: () => isTenant
      ? client.delete('/api/auth/me')
      : client.post('/api/auth/me/request-deletion'),
    onSuccess: () => { if (isTenant) onDeleted?.(); },
  });

  const warningText = role === 'HOSTEL_OWNER'
    ? t('account.ownerDeleteWarningFull', 'This is not just about your own login. Once an admin approves this request: your account will be marked deleted, every hostel you own will be disabled, every supervisor you\'ve added will be disabled, and every tenant currently linked to your hostel(s) will lose access to their tenant portal. This cannot be undone.')
    : role === 'SUPERVISOR'
      ? t('account.supervisorDeleteWarningFull', 'Once an admin approves this request, your account will be marked deleted and disabled, and you will be unassigned from every hostel you currently supervise. This cannot be undone.')
      : t('account.tenantDeleteWarningFull', 'Your account will be permanently deleted immediately — there is no admin review step. You will lose access to your tenant portal, and this cannot be undone.');

  const headerLabel = isTenant ? t('account.deleteAccount', 'Delete Account') : t('account.requestDeletion', 'Request Account Deletion');
  const confirmLabel = isTenant ? t('account.deleteAccount', 'Delete Account') : t('account.requestDeletion', 'Request Deletion');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-chrome-topbar-border">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />
            <span className="font-semibold text-sm">{headerLabel}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={18} />
          </button>
        </div>

        {deletionMutation.isSuccess && !isTenant ? (
          <div className="px-5 py-8 text-center">
            <p className="text-green-600 font-semibold text-sm">
              {t('account.deletionPending', 'Your account deletion request has been submitted. An admin will review it shortly.')}
            </p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-medium hover:opacity-90 transition"
            >
              {t('common.close', 'Close')}
            </button>
          </div>
        ) : (
          <div className="px-5 py-5 flex flex-col gap-4">
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 leading-relaxed">{warningText}</p>
            </div>

            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              {t('account.deleteConfirmCheckbox', 'I understand the consequences and want to proceed.')}
            </label>

            {deletionMutation.isError && (
              <p className="text-red-500 text-xs">
                {(deletionMutation.error as Error)?.message
                  ?? t('common.genericError', 'Something went wrong. Please try again.')}
              </p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => deletionMutation.mutate()}
                disabled={!confirmed || deletionMutation.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40 transition"
              >
                {deletionMutation.isPending ? t('common.loading') : confirmLabel}
              </button>
              <button onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
